import React, { useState } from "react";
import {
  CheckCircle,
  Circle,
  PencilSimple,
} from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";


const FIELDS = Object.freeze([
  { key: "serial_number", label: "Numéro de série", source: "serialNumber", type: "text" },
  { key: "installation_date", label: "Date de livraison", source: "desiredDate", type: "datetime-local" },
  { key: "site_name", label: "Site", source: "siteName", type: "text" },
  { key: "address", label: "Adresse", source: "address", type: "text" },
  { key: "postal_code", label: "Code postal", source: "postalCode", type: "text" },
  { key: "city", label: "Ville", source: "city", type: "text" },
  { key: "access_instructions", label: "Accès au site", source: "accessInstructions", type: "textarea" },
  { key: "implementation_notes", label: "Implantation précise", source: "implementationNotes", type: "textarea" },
  { key: "notes", label: "Mission / consignes", source: "notes", type: "textarea" },
]);


function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}


function displayDate(value) {
  if (!value) return "À compléter";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}


function inputDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}



function FieldEditor({ field, value, onClose, onSave, saving }) {
  const [draft, setDraft] = useState(
    field.type === "datetime-local" ? inputDate(value) : (value ?? "")
  );

  const submit = async (event) => {
    event.preventDefault();
    const nextValue = field.type === "datetime-local" && draft
      ? new Date(draft).toISOString()
      : draft;
    await onSave({ [field.key]: nextValue });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[2147482500] flex items-center justify-center bg-black/35 p-4" role="presentation">
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-[16px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 text-[var(--do-ink)] shadow-[var(--do-shadow-card)]"
        role="dialog"
        aria-modal="true"
        aria-label={field.label}
      >
        <div className="label-eyebrow">Préparation installation</div>
        <h3 className="mt-1 font-heading text-xl font-black text-[var(--do-text)]">{field.label}</h3>

        <div className="mt-5">
          {field.type === "textarea" ? (
            <Textarea
              autoFocus
              rows={5}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              className="rounded-[10px] border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)] focus-visible:ring-[var(--do-accent)]"
            />
          ) : (
            <Input
              autoFocus
              type={field.type}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              className="h-11 rounded-[10px] border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)] focus-visible:ring-[var(--do-accent)]"
            />
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="actaSecondary" disabled={saving} onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" variant="actaPrimary" disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </form>
    </div>
  );
}


export default function InstallationPreparationChecklist({
  installation,
  onSave,
  onFinalize,
  saving = false,
}) {
  const [editingField, setEditingField] = useState(null);
  const completed = FIELDS.filter((field) => hasValue(installation[field.source])).length;
  const ready = completed === FIELDS.length;

  const displayValue = (field) => {
    if (field.type === "datetime-local") {
      return displayDate(installation[field.source]);
    }
    return installation[field.source] || "À compléter";
  };

  return (
    <Card variant="acta" className="p-5 sm:p-6" data-testid="installation-preparation-checklist">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="label-eyebrow">Préparation guidée</div>
          <h2 className="mt-1 font-heading text-xl font-black text-[var(--do-text)]">Informations à valider</h2>
          <p className="mt-1 text-sm text-[var(--do-muted)]">
            {completed}/{FIELDS.length} éléments complétés. Cliquez sur une ligne pour la modifier.
          </p>
        </div>
        <span className="rounded-full bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-accent)]">
          {Math.round((completed / FIELDS.length) * 100)} %
        </span>
      </div>

      <div className="mt-5 divide-y divide-[var(--do-border)] rounded-xl border border-[var(--do-border)]">
        {FIELDS.map((field) => {
          const done = hasValue(installation[field.source]);
          return (
            <button
              type="button"
              key={field.key}
              onClick={() => setEditingField(field)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-[var(--do-surface-soft)]"
              data-testid={`installation-preparation-field-${field.key}`}
            >
              {done ? (
                <CheckCircle size={20} weight="fill" className="shrink-0 text-[var(--do-accent)]" />
              ) : (
                <Circle size={20} className="shrink-0 text-[var(--do-muted)]" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-black uppercase tracking-wide text-[var(--do-muted)]">{field.label}</div>
                <div className={`mt-0.5 truncate text-sm font-semibold ${done ? "text-[var(--do-text)]" : "text-[var(--do-accent)]"}`}>
                  {displayValue(field)}
                </div>
              </div>
              <PencilSimple size={18} />
            </button>
          );
        })}
      </div>

      <Button
        type="button"
        variant="actaPrimary"
        className="mt-5 w-full"
        disabled={!ready || saving}
        onClick={onFinalize}
        data-testid="installation-finalize-preparation"
      >
        {saving ? "Validation…" : "Terminer la préparation"}
      </Button>

      {!ready && (
        <p className="mt-2 text-center text-xs text-[var(--do-muted)]">
          Complétez tous les éléments obligatoires avant de générer le bon de livraison.
        </p>
      )}

      {editingField && (
        <FieldEditor
          field={editingField}
          value={installation[editingField.source]}
          saving={saving}
          onClose={() => setEditingField(null)}
          onSave={onSave}
        />
      )}
    </Card>
  );
}
